import { api } from 'boot/axios'
import packageInfo from '../../package.json'
import { defineStore } from "pinia";
import { useAuthStore } from "stores/auth";

export const useFeedbackStore = defineStore('feedback', {
  persist: true,
  state: (): FeedbackState => ({
    nextFeedback: new Date(),
    cooldown: 1000 * 60 * 60 * 24 // 1 day
  }),
  getters: {
  },
  actions: {
    /**
     * Returns true if the user should be prompted to give feedback
     * There is a 10% chance of this happening, and the user must wait until the nextFeedback date
     * @returns {boolean}
     */
    rollFeedback(): boolean {
      return new Date() > this.nextFeedback && Math.random() < 0.1;
    },
    /**
     * Sends feedback to the server. If reset is true, the nextFeedback date is updated.
     * @param {Feedback} feedback
     * @param {boolean} reset
     * @returns {Promise<void>}
     */
    async sendFeedback(feedback: Feedback, reset: boolean) {
      try {
        feedback = prepFeedback(feedback)
        await api.post("/api/v2/feedback", feedback)
      } catch (e) {
        console.debug(e);
      } finally {
        if (reset) {
          this.updateNextFeedback()
        }
      }
    },
    /**
     * Updates the nextFeedback date to the current date + cooldown
     */
    updateNextFeedback(): void {
      this.nextFeedback = new Date(new Date().getTime() + this.cooldown)
    },
    /**
     * Resets the nextFeedback date to the current date
     */
    async dataRefresh(): Promise<void> {
      this.nextFeedback = new Date()
    }
  }
})

const prepFeedback = ((feedback: Feedback): Feedback => {
  const authStore = useAuthStore();

  if (!feedback.version) {
    feedback.version = getAppVersion()
  }
  if (!feedback.deployment) {
    feedback.deployment = getDeployment()
  }
  if (!feedback.origin) {
    feedback.origin = getOrigin()
  }
  if (!feedback.date) {
    const d = new Date();
    feedback.date = d.getTime();
  }
  if (!feedback.employeeID) {
    feedback.employeeID = Number(authStore.employeeId)
  }
  return feedback
});

const getAppVersion = ((): string => {
  return packageInfo.version
})

const getDeployment = ((): string => {
  if (packageInfo.isMobile) {
    return "mobile"
  } else {
    return "web"
  }
});

const getOrigin = ((): string => {
  return window.origin
});

export type Feedback = {
  score: number | null;
  employeeID: number | null;
  patientRecordID: number | null;
  comments: string | null;
  source: string | null;
  origin: string | null;
  version: string | null;
  deployment: string | null;
  date: Date | number | null;
}

type FeedbackState =  {
  nextFeedback: Date;
  cooldown: number;
}
